import { useState, useEffect, useRef, useCallback, memo } from 'react';
import Navega from '../componentes/Nav';
import Footer from '../componentes/footer';
import "../estilos/Styresenas.css";
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';
import { useSeo } from '../seo';
const CLOUD_NAME = 'dndjwrnkq';
const UPLOAD_PRESET = 'pacific_tours';
const LIMITE_TEXTO = 180;

const FORM_VACIO = { nombre: '', apellido: '', comentario: '', puntuacion: 0, imagen: '' };

const TRIPADVISOR_URL =
  'https://www.tripadvisor.com/Attraction_Review-g309240-d34317655-Reviews-Pacific_Tour_Cr-Liberia_Province_of_Guanacaste.html';

const EstrellasPicker = memo(({ valor, onChange }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="estrellas-picker">
      {[1,2,3,4,5].map(n => (
        <button
          key={n}
          type="button"
          className={`estrella-btn ${n <= (hover || valor) ? 'activa' : ''}`}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          aria-label={`${n} estrella${n > 1 ? 's' : ''}`}
        >★</button>
      ))}
    </div>
  );
});

const EstrellasDisplay = memo(({ valor }) => (
  <span className="estrellas-display">
    {[1,2,3,4,5].map(n => (
      <span key={n} className={n <= valor ? 'estrella-llena' : 'estrella-vacia'}>★</span>
    ))}
  </span>
));

const ImageModal = memo(({ src, onClose }) => {
  useEffect(() => {
    if (!src) return;
    const handler = e => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [src, onClose]);

  if (!src) return null;
  return (
    <div className="img-modal-overlay" onClick={onClose}>
      <div className="img-modal" onClick={e => e.stopPropagation()}>
        <button className="img-modal-close" onClick={onClose}>✕</button>
        <img src={src} alt="imagen comentario" />
      </div>
    </div>
  );
});

const formatearSlug = (slug) =>
  slug?.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') ?? '';

const TarjetaComentario = memo(({ comentario }) => {
  const [expandido, setExpandido] = useState(false);
  const [modalImg, setModalImg] = useState(null);
  const cerrarModal = useCallback(() => setModalImg(null), []);

  const texto = comentario.comentario || '';
  const largo = texto.length > LIMITE_TEXTO;
  const fechaFormateada = comentario.fecha
    ? new Date(comentario.fecha).toLocaleDateString('es-CR', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  return (
    <>
      <div className="tarjeta-comentario">
        {comentario.tour_slug && (
          <div className="badge-tour-esquina">🌿 {formatearSlug(comentario.tour_slug)}</div>
        )}
        <div className="tarjeta-header">
          <div className="tarjeta-avatar">
            <span className="avatar-inicial">{(comentario.nombre || '?')[0].toUpperCase()}</span>
          </div>
          <div className="tarjeta-info">
            <strong className="tarjeta-nombre">{comentario.nombre} {comentario.apellido}</strong>
            <span className="tarjeta-fecha">{fechaFormateada}</span>
          </div>
          <EstrellasDisplay valor={comentario.puntuacion} />
        </div>
        <p className="tarjeta-texto">
          {expandido || !largo ? texto : texto.slice(0, LIMITE_TEXTO) + '…'}
        </p>
        {largo && (
          <button className="btn-leer-mas" onClick={() => setExpandido(v => !v)}>
            {expandido ? 'Leer menos ▲' : 'Leer más ▼'}
          </button>
        )}
        {comentario.imagen && (
          <div className="tarjeta-imagen-tour" onClick={() => setModalImg(comentario.imagen)}>
            <img src={comentario.imagen} alt="foto del tour" loading="lazy" />
            <span className="tarjeta-imagen-hint">Tocá para ampliar</span>
          </div>
        )}
      </div>
      <ImageModal src={modalImg} onClose={cerrarModal} />
    </>
  );
});

const TarjetaSkeleton = () => (
  <div className="tarjeta-comentario" style={{ minHeight: 160, opacity: 0.5, animation: 'pulse 1.5s ease-in-out infinite' }} />
);

const TripadvisorHighlight = memo(({ esIngles, onEscribirAqui }) => (
  <div className="trip-highlight">
    <div className="trip-highlight-left">
      <img
        src="https://cdn.simpleicons.org/tripadvisor/ffffff"
        alt="Tripadvisor"
        width="34"
        height="34"
        loading="lazy"
        className="trip-highlight-logo"
      />
      <div className="trip-highlight-text">
        <span className="trip-highlight-score">5,0</span>
        <span className="trip-highlight-bubbles" aria-hidden="true">
          {[1,2,3,4,5].map(n => <span key={n} className="trip-highlight-bubble" />)}
        </span>
        <span className="trip-highlight-label">
          {esIngles ? 'Excellent · Travelers\' Choice' : 'Excelente · Elección de Viajeros'}
        </span>
      </div>
    </div>
    <div className="trip-highlight-actions">
      <a 
        href={TRIPADVISOR_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="trip-highlight-cta trip-highlight-cta-outline"
      >
        {esIngles ? 'Review on Tripadvisor →' : 'Opinar en Tripadvisor →'}
      </a>
    </div>
  </div>
));

function Resenas() {
  const { i18n } = useTranslation();
  const esIngles = i18n.language === 'en';

  useSeo({ pagina: 'resenas' });

  const [resenas, setResenas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [formAbierto, setFormAbierto] = useState(false);
  const [subiendoImagen, setSubiendoImagen] = useState(false);
  const [previewImagen, setPreviewImagen] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const formRef = useRef(null);

  const cargarResenas = useCallback(() => {
    setCargando(true);
    fetch(`${API_URL}/resenas/`, { cache: 'no-store' })
      .then(r => r.json())
      .then(data => setResenas(Array.isArray(data) ? data : []))
      .catch(err => console.error(err))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => { cargarResenas(); }, [cargarResenas]);

  useEffect(() => {
    if (formAbierto && formRef.current) {
      setTimeout(() => formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, [formAbierto]);

  const handleChange = useCallback(e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }, []);

  const abrirFormulario = useCallback(() => setFormAbierto(true), []);

  const handleImagenCloudinary = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSubiendoImagen(true);
    setError('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('upload_preset', UPLOAD_PRESET);
    try {
      const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
        method: 'POST', body: fd
      });
      const data = await res.json();
      if (!data.secure_url) throw new Error('Sin URL');
      setPreviewImagen(data.secure_url);
      setForm(f => ({ ...f, imagen: data.secure_url }));
    } catch {
      setError(esIngles ? 'Error uploading image.' : 'Error al subir la imagen.');
    } finally {
      setSubiendoImagen(false);
    }
  }, [esIngles]);

  const handleSubmit = useCallback(async e => {
    e.preventDefault();
    setError('');
    if (!form.nombre.trim() || !form.apellido.trim() || !form.comentario.trim()) {
      setError(esIngles ? 'Please fill in all required fields.' : 'Por favor completa todos los campos requeridos.');
      return;
    }
    if (form.puntuacion === 0) {
      setError(esIngles ? 'Please select a rating.' : 'Por favor selecciona una puntuación.');
      return;
    }
    setEnviando(true);
    try {
      const res = await fetch(`${API_URL}/resenas/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: form.nombre.trim(),
          apellido: form.apellido.trim(),
          comentario: form.comentario.trim(),
          puntuacion: form.puntuacion,
          imagen: form.imagen || '',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const nueva = await res.json();
      setResenas(prev => [nueva, ...prev]);
      setForm(FORM_VACIO);
      setPreviewImagen(null);
      setFormAbierto(false);
    } catch (err) {
      console.error(err);
      setError(esIngles ? 'Something went wrong. Please try again.' : 'Ocurrió un error. Intenta nuevamente.');
    } finally {
      setEnviando(false);
    }
  }, [form, esIngles]);

  const cancelar = useCallback(() => {
    setFormAbierto(false);
    setForm(FORM_VACIO);
    setPreviewImagen(null);
    setError('');
  }, []);

  const ocupado = enviando || subiendoImagen;

  return (
    <>
      <Navega />

      <div className="resenas-page">
        <div className="resenas-hero">
          <p className="resenas-eyebrow">{esIngles ? 'Community.' : 'Comunidad.'}</p>
          <h1 className="resenas-titulo">
            {esIngles ? 'Reviews & Experiences' : 'Reseñas y Experiencias'}
          </h1>
          <p className="resenas-subtitulo">
            {esIngles
              ? 'Real stories from real travelers in Costa Rica'
              : 'Historias reales de viajeros en Costa Rica'}
          </p>
          <button
            className="btn-abrir-form"
            onClick={() => setFormAbierto(v => !v)}
            aria-expanded={formAbierto}
          >
            {formAbierto
              ? (esIngles ? '✕ Close form' : '✕ Cerrar formulario')
              : (esIngles ? '✏️ Add a review' : '✏️ Agregar comentario')}
          </button>
        </div>

        <TripadvisorHighlight esIngles={esIngles} onEscribirAqui={abrirFormulario} />

        <div
          className={`resenas-form-collapsible ${formAbierto ? 'abierto' : ''}`}
          ref={formRef}
          aria-hidden={!formAbierto}
        >
          <div className="resenas-form-inner">
            <h2 className="form-titulo">
              {esIngles ? 'Leave your review' : 'Deja tu reseña'}
            </h2>
            <form className="resenas-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>{esIngles ? 'First name' : 'Nombre'} *</label>
                  <input type="text" name="nombre" value={form.nombre} onChange={handleChange}
                    placeholder={esIngles ? 'Your name' : 'Tu nombre'} />
                </div>
                <div className="form-group">
                  <label>{esIngles ? 'Last name' : 'Apellido'} *</label>
                  <input type="text" name="apellido" value={form.apellido} onChange={handleChange}
                    placeholder={esIngles ? 'Your last name' : 'Tu apellido'} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{esIngles ? 'Rating' : 'Puntuación'} *</label>
                  <EstrellasPicker
                    valor={form.puntuacion}
                    onChange={v => setForm(f => ({ ...f, puntuacion: v }))}
                  />
                </div>
                <div className="form-group">
                  <label>{esIngles ? 'Photo (optional)' : 'Foto (opcional)'}</label>
                  <label className="file-input-label">
                    <span>
                      {subiendoImagen
                        ? (esIngles ? '⏳ Uploading...' : '⏳ Subiendo...')
                        : previewImagen
                          ? (esIngles ? '✅ Image uploaded' : '✅ Imagen subida')
                          : (esIngles ? 'Choose image...' : 'Elegir imagen...')}
                    </span>
                    <input type="file" accept="image/*" onChange={handleImagenCloudinary}
                      disabled={subiendoImagen} hidden />
                  </label>
                  {previewImagen && (
                    <img src={previewImagen} alt="preview"
                      style={{ marginTop: 8, width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }} />
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>{esIngles ? 'Your comment' : 'Tu comentario'} *</label>
                <textarea name="comentario" value={form.comentario} onChange={handleChange} rows={4}
                  placeholder={esIngles ? 'Share your experience...' : 'Comparte tu experiencia...'} />
              </div>

              {error && <p className="form-error">{error}</p>}

              <div className="form-actions">
                <button type="submit" className="btn-enviar" disabled={ocupado}>
                  {enviando ? (esIngles ? 'Sending...' : 'Enviando...') : (esIngles ? 'Submit review' : 'Enviar reseña')}
                </button>
                <button type="button" className="btn-cancelar" onClick={cancelar}>
                  {esIngles ? 'Cancel' : 'Cancelar'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="resenas-body">
          <div className="resenas-listado">
            <h2 className="listado-titulo">
              {esIngles ? 'All reviews' : 'Todas las reseñas'}
              {!cargando && resenas.length > 0 && (
                <span style={{ fontSize: '0.85rem', fontWeight: 400, marginLeft: 8, opacity: 0.6 }}>
                  ({resenas.length})
                </span>
              )}
            </h2>

            {cargando ? (
              <div className="comentarios-grid">
                {[...Array(3)].map((_, i) => <TarjetaSkeleton key={i} />)}
              </div>
            ) : resenas.length === 0 ? (
              <p className="resenas-vacio">
                {esIngles ? 'No reviews yet. Be the first!' : 'Aún no hay reseñas. ¡Sé el primero!'}
              </p>
            ) : (
              <div className="comentarios-grid">
                {resenas.map(r => <TarjetaComentario key={r.id} comentario={r} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default Resenas;